import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { EsraPipingAPIClient, RetirePipeClassRequest } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';

/** Data passed into the RetirePipeClassDialogComponent via MAT_DIALOG_DATA. */
export interface RetirePipeClassDialogData {
  /** The ID of the pipe class to retire. */
  pipeClassId: string;
  /** The display name of the pipe class, shown in the warning header. */
  pipeClassName: string;
}

@Component({
  selector: 'app-retire-pipe-class-dialog',
  templateUrl: './retire-pipe-class-dialog.component.html',
  styleUrls: ['./retire-pipe-class-dialog.component.scss'],
  standalone: false,
})
export class RetirePipeClassDialogComponent {

  /** Optional user-entered reason for the retirement. */
  reason: string = '';

  /** Maximum characters allowed for the reason field, per backend recommendation. */
  readonly maxReasonLength: number = 500;

  /** True while the retire API call is in-flight. Disables both action buttons. */
  retiring$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Inline error message shown when the API returns 409 (conflict). */
  retireError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<RetirePipeClassDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RetirePipeClassDialogData,
    private readonly esraPipingAPIClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
  ) {}

  /** Closes the dialog without taking any action. */
  onCancel(): void {
    this.dialogRef.close(false);
  }

  /**
   * Calls the retire endpoint with the optional reason text.
   * On success (200): shows a success toast and closes the dialog with `true` so the
   * parent can refresh the search results. On 409 (conflict): shows the backend message
   * inline so the user understands why the action is blocked (e.g. not published).
   * On any other error: shows a generic error toast.
   */
  onConfirmRetire(): void {
    // Clear any previous inline error before retrying.
    this.retireError = null;

    const request: RetirePipeClassRequest = new RetirePipeClassRequest({
      // Send null (not empty string) when no reason was entered.
      reason: this.reason.trim() || undefined,
    });

    this.esraPipingAPIClient
      .retirePipeClass(this.data.pipeClassId, request)
      .pipe(
        indicate(this.retiring$),
        first(),
      )
      .subscribe({
        next: () => {
          this.snackBarService.showInfo(`"${this.data.pipeClassName}" has been retired successfully.`);
          // Return true so the button component knows it should refresh the search results.
          this.dialogRef.close(true);
        },
        error: (err: unknown) => {
          const status: number = (err as any)?.status;

          if (status === 409) {
            // Show the backend conflict message inline — it explains why the retire failed
            // (e.g. the pipe class is already retired or not currently published).
            this.retireError = getApiErrorMessage(err, 'This pipe class cannot be retired in its current state.');
          } else if (status === 404) {
            this.snackBarService.showError('Pipe class not found. It may have been deleted.');
            this.dialogRef.close(false);
          } else {
            this.snackBarService.showError(
              getApiErrorMessage(err, 'An unexpected error occurred while retiring the pipe class.')
            );
          }
        },
      });
  }
}
